import { useQueryClient } from '@tanstack/react-query';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { useStore } from 'jotai';
import { useEffect, useRef } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { isRecordingInProgress } from '@/app-shell/chronicle/hooks/useChronicleSessionControl';
import { isDesktopLogoutWaitForUploadsEnabled } from '@/app-shell/flags/desktop-logout-wait-for-uploads-flag';
import { chronicleSessionIdAtom, isChronicleConnectedAtom } from '@/atoms/chronicle';
import {
  offlineActiveRecordingIdAtom,
  offlineCapturePausedAtom,
} from '@/atoms/offline-recording/sync';
import { stopActiveOfflineCapture } from '@/hooks/desktop/offlineCaptureStop';
import { isLogoutInFlight } from '@/lib/authInFlight';
import {
  hasPendingCaptureSettlement,
  hasPendingManualCaptureStop,
  hasPendingManualCaptureStopForSession,
} from '@/lib/desktopExitCaptureStop';
import { settleDesktopCaptureForLogout } from '@/lib/desktopLogoutCapture';
import {
  closeLogoutConfirmation,
  desktopLogoutConfirmationAtom,
  getActiveDesktopCaptureActivity,
  openCaptureEndingDialog,
} from '@/lib/desktopLogoutConfirmation';
import { chronicleBridge } from '@/tauri/chronicle';
import { tauriWindowController } from '@/tauri/controller/window';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { bridgeErrorKind, surfaceTauriWindow } from '@heidi/plugin-core';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { findActiveSessionFromCache } from '@heidi/session';
import { activeSessionIdAtom } from '@heidi/session/data';

export {
  NATIVE_WRITER_JOIN_MIRROR_MS,
  OFFLINE_CAPTURE_STOP_TIMEOUT_MS,
} from '@/hooks/desktop/offlineCaptureStop';

export const CHRONICLE_DEVICE_STOP_TIMEOUT_MS = 1_500;

const CLOSE_REQUESTED_EVENT = 'app://close-requested';

const closeWhileRecordingMessages = defineMessages({
  waitForUploadsBody: {
    defaultMessage:
      'Your session is still in progress. Heidi will end it before closing.',
    id: 'vI6KF6udZm',
    description: 'Warning shown before closing Heidi while a session is in progress',
  },
  legacyBody: {
    defaultMessage:
      'Your session is still in progress. Closing now may lose your progress.',
    id: 'VJfMb+3D3A',
  },
  waitForUploadsOkLabel: {
    defaultMessage: 'End and close',
    id: 'K5l0pCWoRU',
    description: 'Button to end the active session and close Heidi',
  },
  legacyOkLabel: {
    defaultMessage: 'Close Heidi',
    id: 'wWGlE7FSXG',
  },
});

/**
 * Serialize `set_close_guard_ready` toggles. Mount fires `(true)` and cleanup
 * fires `(false)` as independent fire-and-forget invokes. On a remount (effect
 * deps changing identity, or React 18 StrictMode in dev), the old cleanup's
 * `false` can be processed by Rust *after* the new mount's `true`, leaving
 * `CLOSE_GUARD_READY=false` while a listener is live — Rust then fails open and
 * never traps a close mid-recording. Chaining the invokes guarantees Rust sees
 * them in issue order, so the last-issued (currently-mounted) state wins.
 */
let closeGuardReadyChain: Promise<unknown> = Promise.resolve();
function setCloseGuardReady(ready: boolean): void {
  closeGuardReadyChain = closeGuardReadyChain
    .catch(() => undefined)
    .then(() => tauriWindowController.set_close_guard_ready(ready))
    .catch((error) => {
      logger.error(`[close-guard] set_close_guard_ready(${ready}) failed`, error);
    });
}

/**
 * Show the native OS confirmation dialog (NSAlert on macOS, MessageBox on
 * Windows). Returns true if the user confirmed the close.
 *
 * react-intl owns the copy — Tauri/Rust must never carry user-facing text.
 */
async function confirmCloseWhileRecording(
  intl: IntlShape,
  waitForUploads: boolean
): Promise<boolean> {
  const { ask } = await import('@tauri-apps/plugin-dialog');
  return ask(
    intl.formatMessage(
      waitForUploads
        ? closeWhileRecordingMessages.waitForUploadsBody
        : closeWhileRecordingMessages.legacyBody
    ),
    {
      title: intl.formatMessage({ defaultMessage: 'Close Heidi?', id: '0/D2B5Rjvi' }),
      kind: 'warning',
      okLabel: intl.formatMessage(
        waitForUploads
          ? closeWhileRecordingMessages.waitForUploadsOkLabel
          : closeWhileRecordingMessages.legacyOkLabel
      ),
      cancelLabel: intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwba+bI' }),
    }
  );
}

function showCloseFailureToast(intl: IntlShape): void {
  toast({
    title: intl.formatMessage({
      defaultMessage: "Couldn't close Heidi. Please try again.",
      id: 'qCgV/36Jv6',
    }),
    tone: 'destructive',
  });
}

export function useCloseAppGuard(): void {
  const queryClient = useQueryClient();
  const jotaiStore = useStore();
  // intl is read inside the listener via a ref so a locale change doesn't
  // force the event listener to be torn down and re-registered.
  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;

  useEffect(() => {
    if (!isTauri()) return;

    let unlisten: UnlistenFn | undefined;
    let cancelled = false;
    // Guard against stacking dialogs if a second close request arrives
    // while the first dialog is still open (e.g. rapid Cmd+Q presses).
    let handling = false;

    const handleCloseRequested = async () => {
      let ownsHandling = false;
      try {
        const waitForUploads = isDesktopLogoutWaitForUploadsEnabled();
        if (
          waitForUploads &&
          (isLogoutInFlight() ||
            jotaiStore.get(desktopLogoutConfirmationAtom)?.phase === 'ending')
        ) {
          logger.info('[close-guard] close ignored while ending session');
          return;
        }
        if (handling) return;
        handling = true;
        ownsHandling = true;

        const activeOfflineCaptureId = jotaiStore.get(offlineActiveRecordingIdAtom);
        let shouldConfirmClose: boolean;
        let reason: string | undefined;

        if (waitForUploads) {
          const activity = getActiveDesktopCaptureActivity();
          const liveSessionId = jotaiStore.get(activeSessionIdAtom);
          const micStopping =
            liveSessionId !== null
              ? hasPendingManualCaptureStopForSession(liveSessionId)
              : hasPendingManualCaptureStop();
          const offlineIsActive =
            activeOfflineCaptureId !== null &&
            !jotaiStore.get(offlineCapturePausedAtom) &&
            !micStopping;
          shouldConfirmClose = activity !== null || offlineIsActive;
          reason = activity ?? (offlineIsActive ? 'Offline capture active' : undefined);
        } else {
          const sessionId = getCurrentSessionId() ?? '';
          const chronicleSessionId = jotaiStore.get(chronicleSessionIdAtom);
          const result = isRecordingInProgress({
            sessionId,
            chronicleSessionId,
            queryClient,
          });
          const cachedActiveSession = result.inProgress
            ? null
            : findActiveSessionFromCache(queryClient);
          shouldConfirmClose = result.inProgress || Boolean(cachedActiveSession);
          reason = result.inProgress
            ? result.reason
            : `cached ${cachedActiveSession?.recordingStatus} session ${cachedActiveSession?.sessionId}`;
        }

        if (!shouldConfirmClose) {
          if (waitForUploads && hasPendingCaptureSettlement()) {
            logger.info(
              '[close-guard] close requested while capture settles — ending first'
            );
            openCaptureEndingDialog('closeApp');
            try {
              await settleDesktopCaptureForLogout('closeApp');
              await tauriWindowController.force_close_main_window();
            } finally {
              closeLogoutConfirmation();
            }
          } else {
            logger.info(
              '[close-guard] close requested with no recording in progress — force-closing'
            );
            await stopActiveOfflineCapture(activeOfflineCaptureId, 'closeGuard');
            await tauriWindowController.force_close_main_window();
          }
          return;
        }

        logger.info('[close-guard] Recording in progress — confirming close', { reason });
        // A native dialog opened over a hidden/minimized window cannot be
        // answered. Treat a failed surface attempt as Cancel; `finally` then
        // releases `handling`, so a later close request is not swallowed.
        const surfaced = await surfaceTauriWindow();
        if (!surfaced) {
          logger.warn(
            '[close-guard] Window could not be surfaced — treating close as Cancel'
          );
          showCloseFailureToast(intlRef.current);
          return;
        }
        const confirmed = await confirmCloseWhileRecording(
          intlRef.current,
          waitForUploads
        );
        if (confirmed) {
          const stopConnectedChronicleDevice = async () => {
            // Only a connected Heidi Remote can orphan a device-side stream.
            // Bound the best-effort stop so a flaky BLE link cannot hang quit.
            if (jotaiStore.get(isChronicleConnectedAtom)) {
              await Promise.race([
                chronicleBridge.stopRecording().catch((error) => {
                  logger.warn('[close-guard] Best-effort Chronicle device stop failed', {
                    errorKind: bridgeErrorKind(error),
                  });
                }),
                new Promise<void>((resolve) => {
                  setTimeout(resolve, CHRONICLE_DEVICE_STOP_TIMEOUT_MS);
                }),
              ]);
            }
          };

          const logoutSettlementInProgress =
            isLogoutInFlight() ||
            jotaiStore.get(desktopLogoutConfirmationAtom)?.phase === 'ending';
          if (
            waitForUploads &&
            (logoutSettlementInProgress || hasPendingCaptureSettlement())
          ) {
            if (!logoutSettlementInProgress) {
              openCaptureEndingDialog('closeApp');
            }
            logger.info('[close-guard] confirmed close joined capture settlement');
            try {
              await settleDesktopCaptureForLogout('closeApp');
              if (logoutSettlementInProgress) return;
              await stopConnectedChronicleDevice();
              await tauriWindowController.force_close_main_window();
            } finally {
              if (!logoutSettlementInProgress) closeLogoutConfirmation();
            }
            return;
          }

          if (waitForUploads) {
            openCaptureEndingDialog('closeApp');
            try {
              await settleDesktopCaptureForLogout('closeApp');
              await stopConnectedChronicleDevice();
              await tauriWindowController.force_close_main_window();
            } finally {
              closeLogoutConfirmation();
            }
          } else {
            await stopConnectedChronicleDevice();
            // Same reason as the no-recording branch above, for a capture that
            // was still running rather than paused.
            await stopActiveOfflineCapture(activeOfflineCaptureId, 'closeGuard');
            await tauriWindowController.force_close_main_window();
          }
        }
      } catch (error) {
        logger.error('[close-guard] Failed to handle close request', error);
        // Surface feedback so a failed close (e.g. a force_close_main_window
        // IPC rejection) isn't silent — on Windows/Linux the OS close was
        // already prevented, so without this the window can appear stuck with
        // only a log line.
        showCloseFailureToast(intlRef.current);
      } finally {
        if (ownsHandling) handling = false;
      }
    };

    void (async () => {
      try {
        const handle = await listen(CLOSE_REQUESTED_EVENT, () => {
          void handleCloseRequested();
        });
        if (cancelled) {
          handle();
          return;
        }
        unlisten = handle;
        // Only now that the listener is live do we tell Rust to start
        // intercepting closes. Before this, Rust fails open (native close).
        setCloseGuardReady(true);
      } catch (error) {
        logger.error('[close-guard] Failed to register close listener', error);
      }
    })();

    return () => {
      cancelled = true;
      unlisten?.();
      // Revert Rust to fail-open while no listener is mounted.
      setCloseGuardReady(false);
    };
  }, [queryClient, jotaiStore]);
}
